import { warnOnce } from '@/hen-ui/utils';

/** A child's column span: a positive integer, or `'full'` for the rest of the row. */
export type GridSpan = number | 'full';

export const GRID_MIN_COLUMNS = 1;
export const GRID_MAX_COLUMNS = 20;
/**
 * Default column count used when `columns` is absent or unparseable.
 * Matches `DASHBOARD_GRID_DEFAULT_COLUMNS` in the admin's `dashboard-grid-slots.ts`
 * so that hen-ui and the admin share the same data contract — a dashboard grid
 * config with an unset `columns` field resolves to the same value in both places.
 */
export const GRID_DEFAULT_COLUMNS = 4;

/**
 * Clamp an arbitrary `columns` value to a usable track count.
 *
 * - `undefined` / `null` / `''` → {@link GRID_DEFAULT_COLUMNS} (4), **no warning**
 *   (treated as "not provided"; `''` is common when Puck form fields are cleared).
 * - non-finite / non-number → {@link GRID_DEFAULT_COLUMNS} (4) + dev warn
 * - non-integer → floored + dev warn
 * - `< ` {@link GRID_MIN_COLUMNS} → clamped to 1 + dev warn
 * - `> ` {@link GRID_MAX_COLUMNS} → clamped to 20 + dev warn
 *
 * Faithful port of the dashboard's `normalizeDashboardGridColumnCount`
 * (including the max-20 cap and the 4-column default). The hen-ui Grid
 * component defaults its `columns` prop to 1 independently; this function
 * is called with that defaulted value, so `undefined` only reaches it from
 * direct admin/data-layer calls (where the 4-column default is correct).
 */
export function normalizeGridColumnCount(columns: unknown): number {
  // `undefined`, `null`, and `''` all mean "not provided" — silent default.
  // `''` is common when Puck form fields are cleared and the value serialises
  // to an empty string rather than null.
  if (columns === undefined || columns === null || columns === '') {
    return GRID_DEFAULT_COLUMNS;
  }

  // Numeric strings (e.g. '1', '2') may arrive from Puck-authored DSL config,
  // which serialises numbers as strings — mirror `parseGridSpan`'s `Number()`
  // coercion. Non-numeric strings ('abc') become NaN and fall through to the
  // finite-number guard below (default + dev warn). The exact `''` case is
  // handled by the early return above.
  const value = typeof columns === 'string' ? Number(columns) : columns;

  if (typeof value !== 'number' || !Number.isFinite(value)) {
    warnOnce(
      `Grid/columns-invalid:${String(columns)}`,
      `Grid: \`columns\` must be a finite number; received ${String(
        columns,
      )}. Falling back to ${GRID_DEFAULT_COLUMNS}.`,
    );
    return GRID_DEFAULT_COLUMNS;
  }

  let n = value;
  if (!Number.isInteger(n)) {
    warnOnce(
      `Grid/columns-non-integer:${n}`,
      `Grid: \`columns\` must be an integer; received ${n}. Using ${Math.floor(
        n,
      )}.`,
    );
    n = Math.floor(n);
  }

  if (n < GRID_MIN_COLUMNS) {
    warnOnce(
      `Grid/columns-too-small:${n}`,
      `Grid: \`columns\` must be >= ${GRID_MIN_COLUMNS}; received ${n}. Using ${GRID_MIN_COLUMNS}.`,
    );
    return GRID_MIN_COLUMNS;
  }

  if (n > GRID_MAX_COLUMNS) {
    warnOnce(
      `Grid/columns-too-large:${n}`,
      `Grid: \`columns\` is capped at ${GRID_MAX_COLUMNS}; received ${n}. Using ${GRID_MAX_COLUMNS}.`,
    );
    return GRID_MAX_COLUMNS;
  }

  return n;
}

/**
 * Normalize a raw span entry to `number | 'full' | undefined`.
 * `undefined` signals "no span declared" so the caller can default to 1.
 *
 * Accepts numeric strings (e.g. `'6'`) because admin config fields may
 * serialise numbers as strings — matches `parseGridSpanForPlacement` in the
 * admin's `dashboard-grid-slots.ts`.
 */
export function parseGridSpan(span: unknown): GridSpan | undefined {
  if (span === 'full') return 'full';
  if (typeof span === 'number' && Number.isFinite(span) && span >= 1) {
    return Math.floor(span);
  }
  // Numeric strings: '6' → 6, '2.9' → 2. The 'full' literal is already
  // handled above so it never reaches Number('full') = NaN.
  if (typeof span === 'string') {
    const n = Number(span);
    if (Number.isFinite(n) && n >= 1) return Math.floor(n);
  }
  return undefined;
}

/**
 * Resolve a child's effective span, clamped to what remains in its row so a
 * block never overflows past the last column.
 *
 * - `'full'` (or a span equal to the column count) → the whole row
 * - span `>` column count → full row + dev warn
 * - otherwise → `min(span, columnsRemainingInRow)`, floored at 1
 */
export function resolveColumnSpan(
  span: unknown,
  columnCount: number,
  columnStart: number,
): number {
  const parsed = parseGridSpan(span);
  let resolved: number;
  if (parsed === 'full' || parsed === columnCount) {
    resolved = columnCount;
  } else if (parsed != null) {
    if (parsed > columnCount) {
      warnOnce(
        `Grid/span-exceeds-columns:${parsed}-${columnCount}`,
        `Grid: \`span\` ${parsed} exceeds \`columns\` ${columnCount}; spanning the full row.`,
      );
      resolved = columnCount;
    } else {
      resolved = parsed;
    }
  } else {
    resolved = 1;
  }
  const remaining = columnCount - columnStart + 1;
  return Math.max(1, Math.min(resolved, remaining));
}
